import { Link } from "@inertiajs/react"
import { useState } from "react"

interface Option {
  id: number
  name: string
}

interface ServiceItemType {
  id: number
  name: string
  cost: number
  price: number
}

interface PaymentType {
  id: number
  name: string
}

interface ExpenseType {
  id: number
  name: string
}

interface LogFormData {
  log_type: string
  content: string
  title: string
  request: string
  request_date: string | null
  log_date: string
  due_date: string | null
  results_notes: string
  assigned_to_id: number | null
  significant: boolean
  sensitive: boolean
  urgent: boolean
  customer_user_id: number | null
  vendor_id: number | null
  todo_category_id: number | null
  user_id: number | null
}

export interface TimeEntryFormData {
  service_item_type_id: number | null
  payment_type_id: number | null
  recorded_hours: number
  service_hourly_cost: number
  service_hourly_price: number
  mileage: number
  mileage_rate: number
  mileage_bill_rate: number
  travel_hours: number
  travel_rate: number
  billable: boolean
  notes: string
  vehicle_allowance: string
}

export interface ExpenseFormData {
  expense_type_ref_id: number | null
  amount: number | string
  notes: string
  paid_by: string
  paid_to: string
  approval_notes: string
}

interface NotifiableUser {
  id: number
  name: string
}

interface Props {
  caseId: number
  data: LogFormData
  setData: (
    key: keyof LogFormData,
    value: string | number | boolean | null,
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  users: Option[]
  todo_categories: Option[]
  can_create_restricted: boolean
  can_create_accounting: boolean
  // Optional time/expense props (only on new form for open cases)
  caseOpen?: boolean
  serviceItemTypes?: ServiceItemType[]
  paymentTypes?: PaymentType[]
  expenseTypes?: ExpenseType[]
  timeEntryData?: TimeEntryFormData
  onTimeEntryChange?: (
    key: keyof TimeEntryFormData,
    value: number | string | boolean | null,
  ) => void
  expenseData?: ExpenseFormData
  onExpenseChange?: (
    key: keyof ExpenseFormData,
    value: number | string | boolean | null,
  ) => void
  // Notify checkboxes for Case Update type
  notifiableUsers?: NotifiableUser[]
  caseManager?: NotifiableUser | null
  notifyUserIds?: number[]
  onNotifyUserIdsChange?: (ids: number[]) => void
}

export default function LogForm({
  caseId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  users,
  todo_categories,
  can_create_restricted,
  can_create_accounting,
  caseOpen,
  serviceItemTypes,
  paymentTypes,
  expenseTypes,
  timeEntryData,
  onTimeEntryChange,
  expenseData,
  onExpenseChange,
  notifiableUsers,
  caseManager,
  notifyUserIds,
  onNotifyUserIdsChange,
}: Props) {
  // Auto-expand a sub-section when the server rejected it for a missing
  // required field (Service Type / Paid By) so the inline error is visible.
  const [showTimeEntry, setShowTimeEntry] = useState(
    !!errors?.service_item_type_id || !!errors?.vehicle_allowance,
  )
  const [showExpense, setShowExpense] = useState(!!errors?.paid_by)

  const showTimeExpenseSections =
    caseOpen && serviceItemTypes && timeEntryData && onTimeEntryChange

  const logTypes = [
    { value: "update", label: "Case Update" },
    { value: "todo", label: "Todo" },
    ...(can_create_restricted
      ? [{ value: "restricted", label: "Restricted" }]
      : []),
    ...(can_create_accounting
      ? [{ value: "accounting", label: "Accounting" }]
      : []),
  ]

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!onTimeEntryChange || !serviceItemTypes) return
    const typeId = e.target.value ? parseInt(e.target.value) : null
    onTimeEntryChange("service_item_type_id", typeId)
    const serviceType = serviceItemTypes.find((s) => s.id === typeId)
    if (serviceType) {
      onTimeEntryChange("service_hourly_cost", serviceType.cost)
      onTimeEntryChange("service_hourly_price", serviceType.price)
    }
  }

  // Mirrors CaseLogsController#time_entry_present? — the mileage reimbursement
  // choice is only required once a time entry is actually being created.
  const timeEntryStarted =
    !!timeEntryData &&
    (timeEntryData.recorded_hours > 0 ||
      timeEntryData.service_item_type_id !== null)

  const inputClass = "border-hpi-light-gray w-full rounded border px-3 py-2"
  const labelClass = "mb-1 block text-sm font-medium"

  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="log_type" className="mb-1 block font-medium">
            Log Type
          </label>
          <select
            id="log_type"
            value={data.log_type}
            onChange={(e) => {
              const newType = e.target.value
              setData("log_type", newType)
              if (newType === "update") {
                setData("significant", false)
                setData("sensitive", false)
                setData("urgent", false)
              }
            }}
            className={inputClass}
          >
            {logTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {errors?.log_type && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.log_type.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="log_date" className="mb-1 block font-medium">
            Log Date
          </label>
          <input
            type="datetime-local"
            id="log_date"
            value={data.log_date ? data.log_date.slice(0, 16) : ""}
            onChange={(e) => setData("log_date", e.target.value)}
            className={inputClass}
          />
          {errors?.log_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.log_date.join(", ")}
            </p>
          )}
        </div>
      </div>

      {data.log_type !== "todo" && (
        <div className="mb-4">
          <label htmlFor="content" className="mb-1 block font-medium">
            Content
          </label>
          <textarea
            id="content"
            value={data.content}
            onChange={(e) => setData("content", e.target.value)}
            className={inputClass}
            rows={6}
          />
          {errors?.content && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.content.join(", ")}
            </p>
          )}
        </div>
      )}

      {data.log_type !== "update" && (
        <div className="mb-4 grid gap-4 md:grid-cols-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.sensitive}
              onChange={(e) => setData("sensitive", e.target.checked)}
              className="border-hpi-light-gray rounded"
            />
            <span className="font-medium">Sensitive</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.urgent}
              onChange={(e) => setData("urgent", e.target.checked)}
              className="border-hpi-light-gray rounded"
            />
            <span className="font-medium">Urgent</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.significant}
              onChange={(e) => setData("significant", e.target.checked)}
              className="border-hpi-light-gray rounded"
            />
            <span className="font-medium">Significant</span>
          </label>
        </div>
      )}

      {(data.log_type === "update" || data.log_type === "todo") &&
        notifiableUsers &&
        onNotifyUserIdsChange && (
          <div className="border-hpi-light-gray mb-6 rounded border bg-gray-50 p-4">
            <h3 className="mb-3 font-semibold">Notify</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
              {caseManager && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-green-600">&#10003;</span>
                  <span className="font-semibold">
                    {caseManager.name} (Case Manager)
                  </span>
                </div>
              )}
              {notifiableUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={notifyUserIds?.includes(user.id) || false}
                    onChange={(e) => {
                      const ids = notifyUserIds || []
                      if (e.target.checked) {
                        onNotifyUserIdsChange([...ids, user.id])
                      } else {
                        onNotifyUserIdsChange(
                          ids.filter((id) => id !== user.id),
                        )
                      }
                    }}
                    className="border-hpi-light-gray rounded"
                  />
                  <span>{user.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

      {data.log_type === "todo" && (
        <div className="border-hpi-yellow bg-hpi-yellow/20 mb-6 rounded border p-4">
          <h3 className="mb-4 font-semibold">Todo Details</h3>
          <div className="mb-4">
            <label htmlFor="title" className="mb-1 block font-medium">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={data.title}
              onChange={(e) => setData("title", e.target.value)}
              className={inputClass}
              maxLength={50}
            />
          </div>
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="due_date" className="mb-1 block font-medium">
                Due Date
              </label>
              <input
                type="date"
                id="due_date"
                value={data.due_date ? data.due_date.slice(0, 10) : ""}
                onChange={(e) => setData("due_date", e.target.value || null)}
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="todo_category_id"
                className="mb-1 block font-medium"
              >
                Category
              </label>
              <select
                id="todo_category_id"
                value={data.todo_category_id ?? ""}
                onChange={(e) =>
                  setData(
                    "todo_category_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className={inputClass}
              >
                <option value="">Select...</option>
                {todo_categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="assigned_to_id" className="mb-1 block font-medium">
              Assigned To
            </label>
            <select
              id="assigned_to_id"
              value={data.assigned_to_id ?? ""}
              onChange={(e) =>
                setData(
                  "assigned_to_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select...</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="request" className="mb-1 block font-medium">
              Request Details
            </label>
            <textarea
              id="request"
              value={data.request}
              onChange={(e) => setData("request", e.target.value)}
              className={inputClass}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Time Entry Section - only shown when creating and case is open */}
      {showTimeExpenseSections && (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowTimeEntry(!showTimeEntry)}
            className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue mb-2 flex w-full items-center justify-between rounded border px-4 py-2 text-left font-medium"
          >
            <span>Add Time Entry (Optional)</span>
            <span className="text-lg">{showTimeEntry ? "−" : "+"}</span>
          </button>

          {showTimeEntry && (
            <div className="border-hpi-light-gray rounded border p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>Service Type</label>
                  <select
                    className={inputClass}
                    value={timeEntryData.service_item_type_id ?? ""}
                    onChange={handleServiceTypeChange}
                    required={timeEntryData.recorded_hours > 0}
                  >
                    <option value="">Select service type...</option>
                    {serviceItemTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {errors?.service_item_type_id && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.service_item_type_id.join(", ")}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Recorded Hours</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    className={inputClass}
                    value={timeEntryData.recorded_hours}
                    onChange={(e) =>
                      onTimeEntryChange(
                        "recorded_hours",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>Travel Hours</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    className={inputClass}
                    value={timeEntryData.travel_hours}
                    onChange={(e) =>
                      onTimeEntryChange(
                        "travel_hours",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>Mileage</label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={timeEntryData.mileage}
                    onChange={(e) =>
                      onTimeEntryChange(
                        "mileage",
                        parseInt(e.target.value) || 0,
                      )
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>Mileage Rate ($/mile)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    className={inputClass}
                    value={timeEntryData.mileage_rate}
                    onChange={(e) =>
                      onTimeEntryChange(
                        "mileage_rate",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>Mileage Reimbursement</label>
                  <select
                    className={inputClass}
                    value={timeEntryData.vehicle_allowance}
                    onChange={(e) =>
                      onTimeEntryChange("vehicle_allowance", e.target.value)
                    }
                    required={timeEntryStarted}
                  >
                    <option disabled value="">
                      --- SELECT ---
                    </option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors?.vehicle_allowance && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.vehicle_allowance.join(", ")}
                    </p>
                  )}
                </div>

                {paymentTypes && paymentTypes.length > 0 && (
                  <div>
                    <label className={labelClass}>Payment Type</label>
                    <select
                      className={inputClass}
                      value={timeEntryData.payment_type_id ?? ""}
                      onChange={(e) =>
                        onTimeEntryChange(
                          "payment_type_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                    >
                      <option value="">Select payment type...</option>
                      {paymentTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className={labelClass}>Time Notes</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={timeEntryData.notes}
                  onChange={(e) => onTimeEntryChange("notes", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expense Section - only shown when creating and case is open */}
      {showTimeExpenseSections && expenseData && onExpenseChange && (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowExpense(!showExpense)}
            className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue mb-2 flex w-full items-center justify-between rounded border px-4 py-2 text-left font-medium"
          >
            <span>Add Expense (Optional)</span>
            <span className="text-lg">{showExpense ? "−" : "+"}</span>
          </button>

          {showExpense && (
            <div className="border-hpi-light-gray rounded border p-4">
              <div className="grid gap-4 md:grid-cols-2">
                {expenseTypes && expenseTypes.length > 0 && (
                  <div>
                    <label className={labelClass}>Expense Type</label>
                    <select
                      className={inputClass}
                      value={expenseData.expense_type_ref_id ?? ""}
                      onChange={(e) =>
                        onExpenseChange(
                          "expense_type_ref_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                    >
                      <option value="">Select type...</option>
                      {expenseTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Amount</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={inputClass}
                    value={expenseData.amount}
                    onChange={(e) => onExpenseChange("amount", e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass}>Payment Method</label>
                  <select
                    className={inputClass}
                    value={expenseData.paid_by}
                    onChange={(e) => onExpenseChange("paid_by", e.target.value)}
                    required={Number(expenseData.amount) > 0}
                  >
                    <option value="">--- SELECT ---</option>
                    <option value="HPI AmEx">Company AmEx</option>
                    <option value="Cash">Reimbursement Requested</option>
                    <option value="Vendor">Vendor to Invoice</option>
                  </select>
                  {errors?.paid_by && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.paid_by.join(", ")}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Paid To</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={expenseData.paid_to}
                    onChange={(e) => onExpenseChange("paid_to", e.target.value)}
                    placeholder="e.g., Gas Station, Hotel"
                  />
                </div>

                <div>
                  <label className={labelClass}>Approved By</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={expenseData.approval_notes}
                    onChange={(e) =>
                      onExpenseChange("approval_notes", e.target.value)
                    }
                    placeholder="Name of approver"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass}>Expense Notes</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={expenseData.notes}
                  onChange={(e) => onExpenseChange("notes", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/logs`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
